import { onMounted, onUnmounted, ref } from 'vue'
import { addBreadcrumb, captureError } from '@/utils'

// 页面可见性状态
export interface PageVisibilityState {
  isVisible: boolean
  isHidden: boolean
  lastHiddenTime: number | null
  backgroundDuration: number
}

const pageVisibilityState = ref<PageVisibilityState>({
  isVisible: true,
  isHidden: false,
  lastHiddenTime: null,
  backgroundDuration: 0
})

export function usePageVisibility() {
  // 设置后台时间阈值（毫秒）：超过这个时间需要刷新页面
  const BACKGROUND_THRESHOLD = 5 * 60 * 1000 // 5分钟

  const logVisibilityEvent = (
    message: string,
    detail?: Record<string, any>,
    level: 'info' | 'warning' | 'error' | 'debug' = 'info'
  ) => {
    addBreadcrumb({
      message,
      category: 'app/page-visibility',
      level,
      data: detail
    })
  }

  // 处理页面可见性变化
  const handleVisibilityChange = async () => {
    const isHidden = document.hidden
    const now = Date.now()

    if (isHidden) {
      // 页面进入后台
      pageVisibilityState.value.isVisible = false
      pageVisibilityState.value.isHidden = true
      pageVisibilityState.value.lastHiddenTime = now
      logVisibilityEvent('页面进入后台', { timestamp: now }, 'debug')
    } else {
      // 页面从后台切回前台
      pageVisibilityState.value.isVisible = true
      pageVisibilityState.value.isHidden = false

      const lastHiddenTime = pageVisibilityState.value.lastHiddenTime
      if (lastHiddenTime) {
        const backgroundDuration = now - lastHiddenTime
        pageVisibilityState.value.backgroundDuration = backgroundDuration
        logVisibilityEvent('页面从后台切回前台', {
          backgroundDurationMs: backgroundDuration
        })

        // 如果后台时间超过阈值，重新初始化应用
        if (backgroundDuration > BACKGROUND_THRESHOLD) {
          logVisibilityEvent(
            '后台时间过长，准备重新初始化应用',
            { backgroundDurationMs: backgroundDuration },
            'warning'
          )
          refreshApp()
        }
      }

      pageVisibilityState.value.lastHiddenTime = null
    }
  }

  // iOS Safari 特殊处理：使用 pagehide/pageshow 事件
  const handlePageShow = async () => {
    logVisibilityEvent('页面显示 (pageshow)')
    const lastHiddenTime = pageVisibilityState.value.lastHiddenTime
    if (lastHiddenTime) {
      const backgroundDuration = Date.now() - lastHiddenTime
      if (backgroundDuration > BACKGROUND_THRESHOLD) {
        logVisibilityEvent(
          'iOS Safari 后台时间过长，准备重新初始化应用',
          { backgroundDurationMs: backgroundDuration },
          'warning'
        )
        refreshApp()
      }
    }
    pageVisibilityState.value.isVisible = true
    pageVisibilityState.value.isHidden = false
    pageVisibilityState.value.lastHiddenTime = null
  }

  const handlePageHide = () => {
    logVisibilityEvent('页面隐藏 (pagehide)')
    pageVisibilityState.value.isVisible = false
    pageVisibilityState.value.isHidden = true
    pageVisibilityState.value.lastHiddenTime = Date.now()
  }

  // Android WebView 特殊处理：使用 focus/blur 事件
  const handleWindowFocus = async () => {
    logVisibilityEvent('窗口获得焦点 (focus)')
    const lastHiddenTime = pageVisibilityState.value.lastHiddenTime
    if (lastHiddenTime) {
      const backgroundDuration = Date.now() - lastHiddenTime
      if (backgroundDuration > BACKGROUND_THRESHOLD) {
        logVisibilityEvent(
          'Android WebView 后台时间过长，准备重新初始化应用',
          { backgroundDurationMs: backgroundDuration },
          'warning'
        )
        refreshApp()
      }
    }
    pageVisibilityState.value.isVisible = true
    pageVisibilityState.value.isHidden = false
    pageVisibilityState.value.lastHiddenTime = null
  }

  const handleWindowBlur = () => {
    logVisibilityEvent('窗口失去焦点 (blur)')
    pageVisibilityState.value.isVisible = false
    pageVisibilityState.value.isHidden = true
    pageVisibilityState.value.lastHiddenTime = Date.now()
  }

  // 初始化监听器
  const initVisibilityListeners = () => {
    // 标准的页面可见性API
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // iOS Safari 兼容
    window.addEventListener('pageshow', handlePageShow)
    window.addEventListener('pagehide', handlePageHide)

    // Android WebView 和其他环境兼容
    window.addEventListener('focus', handleWindowFocus)
    window.addEventListener('blur', handleWindowBlur)

    logVisibilityEvent('页面可见性监听器已初始化', undefined, 'debug')
  }

  // 清理监听器
  const cleanupVisibilityListeners = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('pageshow', handlePageShow)
    window.removeEventListener('pagehide', handlePageHide)
    window.removeEventListener('focus', handleWindowFocus)
    window.removeEventListener('blur', handleWindowBlur)

    logVisibilityEvent('页面可见性监听器已清理', undefined, 'debug')
  }

  // 手动刷新应用（提供给外部调用）
  const refreshApp = async () => {
    logVisibilityEvent('触发页面刷新', undefined, 'warning')
    try {
      location.reload()
    } catch (error) {
      if (error instanceof Error) {
        logVisibilityEvent('页面刷新失败', { message: error.message }, 'error')
        captureError(error, {
          feature: 'page-visibility-refresh'
        })
      } else {
        logVisibilityEvent('页面刷新失败', undefined, 'error')
      }
    }
  }

  // 自动初始化和清理
  onMounted(() => {
    initVisibilityListeners()
  })

  onUnmounted(() => {
    cleanupVisibilityListeners()
  })

  return {
    // 状态
    pageVisibilityState,

    // 方法
    initVisibilityListeners,
    cleanupVisibilityListeners,
    refreshApp,

    // 常量
    BACKGROUND_THRESHOLD
  }
}
